import axios from 'axios';
import { getToken, isTokenExpired, logout, refreshAccessToken } from './authService';

// Define base API URL
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Define allowed public URLs (match your middleware)
const allowedUrls = [
    "/ap/admin/login",
    "/ap/admin/forgot-password",
    "/ap/admin/verify",
    "/ap/admin/update-password",
    "/ap/admin/send-reset-code",
    "/ma/user/register",
    "/ma/user/login",
    "/ma/user/social-login",
    "/ma/user/forgot-password",
    "/ma/user/verify",
    "/mp/user/register",
    "/mp/user/login",
    "/mp/user/forgot-password",
    "/mp/user/reset-password",
    "/mp/subscription/create",
];

// Create Axios instance
export const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 60000, // File/image uploads need generous headroom (was 5s, too low)
    headers: { "Content-Type": "multipart/form-data", }
});

// Request Interceptor: Attach Token (if needed)
axiosInstance.interceptors.request.use(
    function (config) {
        const token = getToken();

        // Attach token only if it's not in the public routes
        if (token && !allowedUrls.includes(config.url)) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

// Response Interceptor: auto-refresh on expiry, force logout on account disable
// (mirrors axiosInstance.js — see there for rationale).
axiosInstance.interceptors.response.use(
    function (response) {
        return response;
    },
    async function (error) {
        const original = error?.config || {};
        const status = error?.response?.status;
        const isPublic = allowedUrls.includes(original.url);

        if (status === 403 && error?.response?.data?.code === "ACCOUNT_DISABLED") {
            logout();
            return Promise.reject(error);
        }

        // `getToken()` must be present — a 401 with no token means no session
        // exists, so there is nothing to refresh or log out of (see axiosInstance.js).
        if (status === 401 && !isPublic && !original._retry && getToken() && isTokenExpired()) {
            original._retry = true;
            const newToken = await refreshAccessToken();
            if (newToken) {
                return axiosInstance(original);
            }
            logout();
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);
